import {IonButton, IonCardTitle, IonCol, IonGrid, IonInput, IonLabel, IonPage, IonRow, IonSpinner, IonText, useIonRouter} from '@ionic/react';
import {useEffect, useState} from "react";
import {useParams} from "react-router";
import indexBy from 'underscore/modules/indexBy.js';
import moment from 'moment';

import CustomPage from "./CustomPage";
import CustomField from "../components/CustomField";
import {ActionLink} from "../components/ActionLink";
import {useSideMenuUpdate, useAppUtil} from "../components/sideMenu/SideMenuProvider";
import {useFormInput, validateForm} from "../_/useForms";
import {fetch_pg_8_6, push_pg_8_6, Item_委託建立會員} from "../store/Pg_8_Store";
import {navAwayToPg_1_1} from "./Utils";

import styles from './Pg_8_6.module.scss';

// 伺服器端屬於「連結本身」的錯誤：顯示後不再提供表單
const LINK_LEVEL_ERRORS = [
    '此連結已完成建立帳號，請直接登入',
    '註冊連結已過期，請向商家重新索取',
    '註冊連結不存在或已失效',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const useCreateFields = () => {
    return [
        {
            id: "create_nickname",
            label: "暱稱",
            required: true,
            input: {
                props: {type: "text", placeholder: "暱稱", autocomplete: "nickname"},
                state: useFormInput("")
            }
        },
        {
            id: "create_email",
            label: "Email",
            required: true,
            input: {
                props: {type: "email", placeholder: "Email", autocomplete: "email"},
                state: useFormInput("")
            }
        },
        {
            id: "create_password",
            label: "密碼",
            required: true,
            input: {
                props: {type: "password", placeholder: "密碼 (至少 6 碼)", autocomplete: "new-password"},
                state: useFormInput("")
            }
        },
        {
            id: "create_password_confirm",
            label: "確認密碼",
            required: true,
            input: {
                props: {type: "password", placeholder: "再輸入一次密碼", autocomplete: "new-password"},
                state: useFormInput("")
            }
        },
    ];
};

type PageState = 'loading' | 'form' | 'used' | 'invalid';

const Pg_8_6 = (props) => {
    const pageId = "pg_8_6";
    const {sideMenuOptions} = props;
    const setSideMenu: any = useSideMenuUpdate();
    const appUtil = useAppUtil();
    const router = useIonRouter();

    const {ticketcode}: any = useParams();

    const [pageState, setPageState] = useState<PageState>('loading');
    const [item, setItem] = useState<Item_委託建立會員 | null>(null);
    const [linkMessage, setLinkMessage] = useState<string>('');
    const [serverError, setServerError] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);

    const fields = useCreateFields();
    const keyedFields: any = indexBy(fields, 'id');
    const [errors, setErrors] = useState<any>(false);

    useEffect(() => {
        setSideMenu({options: sideMenuOptions, side: "start", pageName: pageId});
    }, []);

    useEffect(() => {
        let cancelled = false;
        setPageState('loading');
        (async () => {
            const {item, error} = await fetch_pg_8_6({ticketcode});
            if (cancelled) return;
            if (error || !item) {
                setLinkMessage(error?.message || '註冊連結不存在或已失效');
                setPageState('invalid');
                return;
            }
            setItem(item);
            if (item.IsTicketCodeUsed) {
                setPageState('used');
            } else {
                keyedFields.create_nickname.input.state.reset(item.暱稱 ?? '');
                keyedFields.create_email.input.state.reset(item.Email ?? '');
                setPageState('form');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ticketcode]);

    const push_create = async () => {
        if (submitting) return;
        setServerError('');

        const errors = validateForm(fields);
        const nickname = keyedFields.create_nickname.input.state.value;
        const email = keyedFields.create_email.input.state.value;
        const password = keyedFields.create_password.input.state.value;
        const confirm = keyedFields.create_password_confirm.input.state.value;

        if (email !== '' && !EMAIL_RE.test(email)) {
            errors.push({id: 'create_email', message: 'Email 格式不正確'});
        }
        if (password !== '' && password.length < 6) {
            errors.push({id: 'create_password', message: '密碼至少 6 碼'});
        }
        if (password !== '' && confirm !== '' && password !== confirm) {
            errors.push({id: 'create_password_confirm', message: '前後輸入的密碼不一致'});
        }
        setErrors(errors);
        if (errors.length) return;

        setSubmitting(true);
        try {
            const {result, error} = await push_pg_8_6({
                ticketcode,
                create_nickname: nickname,
                create_email: email,
                create_password: password,
            });

            if (error) {
                const message = error.message || '建立失敗，請稍後再試';
                if (LINK_LEVEL_ERRORS.includes(message)) {
                    setLinkMessage(message);
                    setPageState(message === LINK_LEVEL_ERRORS[0] ? 'used' : 'invalid');
                } else {
                    setServerError(message);
                }
                return;
            }

            if (result) {
                await appUtil.toast.present(result.Message || '建立成功，已自動登入。', 3000);
                if (result.Gift?.TokenId) {
                    router.push(`/tabs/1_5/${result.Gift.TokenId}`, "forward", "replace");
                } else {
                    navAwayToPg_1_1(router);
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const expireText = item?.TicketExpireAt ? moment(item.TicketExpireAt).format('YYYY-MM-DD HH:mm') : '';

    return (
        <IonPage id={pageId}>
            <CustomPage name={pageId} showLargeHeader={false} sideMenu={false} backButton={true}>
                <IonGrid className="ion-padding">
                    <IonRow>
                        <IonCol size="12" className={styles.heading}>
                            <IonCardTitle>建立帳號</IonCardTitle>
                            {pageState === 'form' && item &&
                                <h5>填寫以下資料即可建立您的 LABIE.io 帳號。</h5>
                            }
                        </IonCol>
                    </IonRow>

                    {pageState === 'loading' &&
                        <IonRow className="ion-justify-content-center ion-margin-top ion-padding-top">
                            <IonCol size="12" className="ion-text-center">
                                <IonSpinner/>
                            </IonCol>
                        </IonRow>
                    }

                    {pageState === 'invalid' &&
                        <IonRow>
                            <IonCol size="12" className={styles.stateBox}>
                                <IonText color="danger"><h5>{linkMessage}</h5></IonText>
                                <p>若您是從商家取得此連結，請聯絡商家重新發送。</p>
                            </IonCol>
                        </IonRow>
                    }

                    {pageState === 'used' &&
                        <IonRow>
                            <IonCol size="12" className={styles.stateBox}>
                                <h5>此連結已完成建立帳號，請直接登入</h5>
                                {item &&
                                    <p>
                                        {item.暱稱 && <>暱稱：{item.暱稱}<br/></>}
                                        {item.Email && <>Email：{item.Email}</>}
                                    </p>
                                }
                                <IonButton className="custom-button" expand="block" routerLink="/A_1" routerDirection="forward">前往登入</IonButton>
                            </IonCol>
                        </IonRow>
                    }

                    {pageState === 'form' && item &&
                        <>
                            <IonRow>
                                <IonCol size="12">
                                    {item.委託者暱稱 &&
                                        <div className={styles.inviter}>由 {item.委託者暱稱} 邀請</div>
                                    }
                                    {item.Gift &&
                                        <div className={styles.gift}>完成註冊即獲得 {item.Gift.名稱}</div>
                                    }
                                    {expireText &&
                                        <div className={styles.expire}>連結有效至 {expireText}</div>
                                    }
                                </IonCol>
                            </IonRow>

                            <IonRow className="ion-margin-top">
                                <IonCol size="12">
                                    <div className={styles.readonlyField}>
                                        <IonLabel className={styles.hint}>手機號碼 (由邀請連結帶入，不可修改)</IonLabel>
                                        <IonInput readonly={true} value={item.手機_Masked}/>
                                    </div>

                                    {fields.map(field => {
                                        return <CustomField key={field.id} field={field} errors={errors} disabled={submitting}/>;
                                    })}

                                    {serverError &&
                                        <div className={styles.serverError}>{serverError}</div>
                                    }

                                    <IonButton className="custom-button" expand="block" onClick={push_create} disabled={submitting}>
                                        {submitting ? <IonSpinner name="dots"/> : '建立帳號'}
                                    </IonButton>

                                    <div className="ion-text-center ion-margin-top ion-padding-top">
                                        已經有帳號？ <ActionLink link={`/A_1`}><IonText>前往登入</IonText></ActionLink>
                                    </div>
                                </IonCol>
                            </IonRow>
                        </>
                    }
                </IonGrid>
            </CustomPage>
        </IonPage>
    );
};

export default Pg_8_6;
